/* The native host owns durability and submit; the VApp receives callbacks. */
import { client } from '@Utils'
import { HTML_CSS_JS_PROFILE, projectFiles, requireCodingProfile, codingIdentity, requireAnswerProfile } from './project-profile'

const clone = value => JSON.parse(JSON.stringify(value))
const same = (a, b) => JSON.stringify(a) === JSON.stringify(b)

export const indexedDraftStore = () => {
  let opening
  const database = () => {
    if (!opening) {
      opening = new Promise((resolve, reject) => {
        if (!window.indexedDB) return reject(new Error('Local recovery is unavailable.'))
        const request = window.indexedDB.open('myeducator-coding-drafts', 1)
        request.onupgradeneeded = () => request.result.createObjectStore('drafts')
        request.onsuccess = () => resolve(request.result)
        request.onerror = () => reject(new Error('Local recovery is unavailable.'))
        request.onblocked = () => reject(new Error('Local recovery is blocked.'))
      })
    }
    return opening
  }
  const transact = async (mode, callback) => {
    const db = await database()
    return new Promise((resolve, reject) => {
      const transaction = db.transaction('drafts', mode)
      let value
      const request = callback(transaction.objectStore('drafts'))
      request.onsuccess = () => { value = request.result }
      // Resolving only on complete matters: request success is not a durable write.
      transaction.oncomplete = () => resolve(value)
      transaction.onerror = transaction.onabort = () => reject(new Error('Local recovery could not be saved.'))
    })
  }
  return {
    get: key => transact('readonly', store => store.get(key)),
    put: (key, value) => transact('readwrite', store => store.put(value, key)),
    remove: key => transact('readwrite', store => store.delete(key)),
  }
}

export const createCodingPersistence = ({ config, getFiles, setFiles, onState = () => {},
  request = client, draftStore = indexedDraftStore(), timers = window, supportsProfile = () => true }) => {
  let profile = requireCodingProfile(config.definition)
  if (!supportsProfile(config.definition)) throw new Error('The coding component does not support this project.')
  let answer = config.answer ? clone(config.answer) : null
  if (answer) requireAnswerProfile(answer, config.definition)
  let disposed = false
  let terminal = !!config.readonly
  let conflict = false
  let initialized = !!answer || terminal || !!config.preview
  let idleTimer, maxTimer, retryTimer
  let chain = Promise.resolve()
  let pending = 0
  let localDraft = null
  let recoveryMismatch = false
  let history = { versions: [], checkpoints: [], upgradeAvailable: false,
    definition: profile !== HTML_CSS_JS_PROFILE ? clone(config.definition) : config.definition,
    ...(profile !== HTML_CSS_JS_PROFILE ? { runtimeArtifactId: config.runtimeArtifactId || null } : {}) }
  let submitting = false
  let initializing = false
  let initialization
  let historyBusy = false
  let historyGeneration = 0
  let terminalUnsubmitted = false
  let unsafeLocal = false
  let lastStatus = 'loading', lastMessage = 'Loading saved code…'
  const announce = (status, message = '', extra = {}) => {
    lastStatus = status
    lastMessage = message
    onState({
    status, message, answer: answer && clone(answer), ...history,
    terminal, conflict, initializing, historyBusy,
    readOnly: terminal || conflict || submitting || initializing || historyBusy,
    unsubmittedLocal: terminalUnsubmitted,
    localDraft: localDraft && clone(localDraft),
    ...extra,
    })
  }
  const fixedFiles = value => projectFiles(value, profile)
  const identity = () => codingIdentity(history.definition)
  const recoveryKey = () => profile !== HTML_CSS_JS_PROFILE
    ? `${config.recoveryKey}:${profile.id}:${history.definition.runtimeArtifactId}:${answer ? answer.versionId : 'practice'}`
    : config.recoveryKey
  const compatibleDraft = draft => {
    if (!draft || !draft.files) return false
    if (profile === HTML_CSS_JS_PROFILE) return true
    return draft.schemaVersion === 2 && draft.runnerProfile === profile.id &&
      draft.runtimeArtifactId === history.definition.runtimeArtifactId &&
      draft.versionId === (answer && answer.versionId) && draft.starterId === (answer && answer.starterId) &&
      Object.keys(draft.files).length === profile.files.length &&
      profile.files.every(name => typeof draft.files[name] === 'string')
  }
  const files = () => fixedFiles(getFiles())
  const dirty = () => !initialized || (!terminal && !config.preview && (!answer || !same(files(), fixedFiles(answer.files))))
  const clearTimers = () => {
    timers.clearTimeout(idleTimer)
    timers.clearTimeout(maxTimer)
    timers.clearTimeout(retryTimer)
    idleTimer = maxTimer = retryTimer = null
  }
  const adopt = (response, { replaceFiles = false } = {}) => {
    const data = response.data || response
    if (!data.success || !data.answer) throw new Error('The server did not acknowledge the coding answer.')
    const definition = data.definition === undefined ? history.definition : data.definition
    const nextProfile = requireAnswerProfile(data.answer, definition)
    if (!supportsProfile(definition)) throw new Error('The coding component does not support this project.')
    const oldRecoveryKey = recoveryKey()
    profile = nextProfile
    answer = clone(data.answer)
    history = { versions: data.versions || [], checkpoints: data.checkpoints || [],
      upgradeAvailable: !!data.upgradeAvailable,
      definition: profile !== HTML_CSS_JS_PROFILE ? clone(definition) : definition,
      ...(profile !== HTML_CSS_JS_PROFILE ? { runtimeArtifactId: data.runtimeArtifactId || null } : {}) }
    if (oldRecoveryKey !== recoveryKey()) { localDraft = null; recoveryMismatch = false }
    terminal = terminal || !!config.readonly || !!data.terminal
    initialized = true
    if (config.answerInput) config.answerInput.value = JSON.stringify(answer)
    if (replaceFiles) setFiles(clone(answer.files))
    announce(dirty() ? 'unsaved' : 'saved', dirty() ? 'Saving your latest edits…' : 'Saved')
    return data
  }
  const enqueue = work => {
    pending += 1
    const run = chain.catch(() => {}).then(work)
    chain = run.finally(() => { pending -= 1 })
    // The caller still receives rejection; keep the serialized tail handled.
    chain.catch(() => {})
    return run
  }
  const durable = async (snapshot = clone(files())) => {
    if (terminal) return true
    if (!config.recoveryKey) throw new Error('Local recovery scope is unavailable.')
    const key = recoveryKey()
    const generation = historyGeneration
    // A failed read can be transient. Recheck before retrying a write, but never
    // clear a real mismatch merely because the server accepted other source.
    if (recoveryMismatch) await loadRecovery()
    if (key !== recoveryKey() || generation !== historyGeneration) throw new Error('The selected coding version changed before local recovery could be saved.')
    if (recoveryMismatch) throw new Error('An incompatible local recovery copy is retained. Save to the server before running.')
    const value = { schemaVersion: 1, versionId: answer && answer.versionId,
      answerRevision: answer && answer.answerRevision,
      versionRevision: answer && answer.versionRevision,
      starterId: answer && answer.starterId, files: snapshot, ...identity() }
    // Once recovery is offered, preserve that copy until an explicit recovery
    // has succeeded. New live drafts use a separate key in the meantime.
    await draftStore.put(localDraft ? recoveryKey() + ':current' : recoveryKey(), value)
    return clone(snapshot)
  }
  const handleFailure = async error => {
    const data = error.response && error.response.data || {}
    if (data.terminal || data.error === 'submission_finalized' || data.error === 'deadline_expired') {
      const unsaved = clone(files())
      const differs = !answer || !same(fixedFiles(unsaved), fixedFiles(answer.files))
      terminal = true
      clearTimers()
      let durableRecovery = !differs
      if (differs) {
        localDraft = { schemaVersion: 1, versionId: answer && answer.versionId,
          answerRevision: answer && answer.answerRevision, versionRevision: answer && answer.versionRevision,
          starterId: answer && answer.starterId, files: unsaved, ...identity() }
        try {
          if (recoveryMismatch) throw new Error('An incompatible local recovery copy is retained.')
          await draftStore.put(recoveryKey() + ':current', clone(localDraft))
          durableRecovery = true
        } catch (storageError) { unsafeLocal = true }
      }
      let authoritative = false
      try {
        const current = await request.get(config.stateUrl)
        if ((current.data || current).answer) {
          adopt(current, { replaceFiles: durableRecovery })
          authoritative = true
        }
      } catch (readError) { /* Keep and label the unsubmitted copy. */ }
      terminal = true
      terminalUnsubmitted = differs && (!durableRecovery || !authoritative)
      if (terminalUnsubmitted) {
        setFiles(unsaved)
        announce('terminal-local', unsafeLocal
          ? 'This assessment cannot accept more saves. The editor shows an unsubmitted local copy. Copy it before leaving.'
          : 'This assessment cannot accept more saves. The editor shows an unsubmitted local copy retained for recovery.')
      } else {
        announce('terminal', 'This assessment cannot accept more saves. Showing its accepted server code; unsent edits are retained separately.')
      }
    } else if (data.error === 'revision_conflict') {
      conflict = true
      clearTimers()
      announce('conflict', 'Code changed in another tab. Your local draft is retained; reload to continue.')
    } else {
      announce('error', data.message || 'Code could not be saved. Retry before submitting.')
    }
    return error
  }
  const loadRecovery = async () => {
    const key = recoveryKey()
    const generation = historyGeneration
    try {
      const storedDraft = await draftStore.get(key)
      const currentDraft = await draftStore.get(key + ':current')
      if (key !== recoveryKey() || generation !== historyGeneration) return
      recoveryMismatch = profile !== HTML_CSS_JS_PROFILE && [storedDraft, currentDraft].some(draft => draft && !compatibleDraft(draft))
      const needsRecovery = draft => compatibleDraft(draft) &&
        (config.preview || !same(fixedFiles(draft.files), fixedFiles(answer.files)))
      // A matching durable copy is already on the server. Do not offer it as
      // recovery, but preserve a distinct older copy if :current was saved.
      localDraft = needsRecovery(currentDraft) ? currentDraft : needsRecovery(storedDraft) ? storedDraft : null
      if (localDraft) {
        announce('recovery', config.preview ? 'A local practice draft is available to restore.'
          : 'A local draft is available. Copy it into a new version to recover.',
          { localDraft: clone(localDraft) })
      } else if (recoveryMismatch) {
        announce('local-unavailable', 'A local draft belongs to another coding runtime or version and has been retained. Save to the server before running.')
      }
    } catch (error) {
      if (key !== recoveryKey() || generation !== historyGeneration) return
      if (profile !== HTML_CSS_JS_PROFILE) recoveryMismatch = true
      announce('local-unavailable', 'Local recovery is unavailable. Run requires an acknowledged server save.')
    }
  }
  const initialize = () => {
    if (initialization) return initialization
    initializing = !config.preview
    announce('loading', 'Loading saved code…')
    initialization = enqueue(async () => {
    try {
    if (disposed) throw new Error('Coding host was closed.')
    if (config.preview) {
      initialized = true
      try {
        const stored = await draftStore.get(recoveryKey())
        recoveryMismatch = profile !== HTML_CSS_JS_PROFILE && !!stored && !compatibleDraft(stored)
        if (compatibleDraft(stored) && profile.files.every(key => typeof stored.files[key] === 'string')) localDraft = stored
      } catch (error) {
        if (profile !== HTML_CSS_JS_PROFILE) recoveryMismatch = true
        // Run reports unavailable recovery before executing.
      }
      announce('preview', 'Practice edits do not change the assignment. Run saves a local recovery copy.')
      return null
    }
    if (terminal) {
      const data = (await request.get(config.stateUrl)).data
      if (data.answer) adopt(data, { replaceFiles: true })
      else history = { versions: data.versions || [], checkpoints: data.checkpoints || [], upgradeAvailable: false,
        ...(profile !== HTML_CSS_JS_PROFILE ? { definition: history.definition } : {}) }
      announce('readonly', 'Saved code is read-only.')
      return answer
    }
    try {
      const response = await request.post(config.initializeUrl, identity())
      adopt(response, { replaceFiles: true })
      await loadRecovery()
      return answer
    } catch (error) { throw await handleFailure(error) }
    } finally {
      initializing = false
      announce(lastStatus, lastMessage)
    }
    })
    initialization.catch(() => { initialization = null })
    return initialization
  }
  const acknowledgedSave = async (snapshot = null) => {
    if (disposed) throw new Error('Coding host was closed.')
    if (terminal || conflict) throw new Error('The coding project is read-only; reload to continue.')
    if (!initialized || !answer) throw new Error('The coding project has not initialized.')
    while (snapshot ? !same(snapshot, fixedFiles(answer.files)) : dirty()) {
      const targetFiles = clone(snapshot || files())
      const base = clone(answer)
      announce('saving', 'Saving code…')
      try {
        const response = await request.post(config.saveUrl, {
          operation: 'save', versionId: base.versionId, versionRevision: base.versionRevision,
          answerRevision: base.answerRevision, files: targetFiles, ...identity(),
        })
        const acknowledgement = (response.data || response).answer
        if (!acknowledgement || acknowledgement.versionId !== base.versionId
            || acknowledgement.versionRevision !== base.versionRevision + 1
            || acknowledgement.answerRevision !== base.answerRevision + 1
            || !same(fixedFiles(acknowledgement.files), fixedFiles(targetFiles))) {
          throw new Error('The server acknowledged a different coding revision. Reload before continuing.')
        }
        requireAnswerProfile(acknowledgement, history.definition)
        adopt(response)
      } catch (error) {
        const status = error.response && error.response.status
        // A lost response may follow a committed save. Verify the exact expected
        // revision and files; never promote a newer tab's state as our own save.
        if (!status || status >= 500 || status === 409) {
          try {
            const current = (await request.get(config.stateUrl)).data
            const accepted = current.answer
            if (accepted && accepted.versionId === base.versionId
                && accepted.answerRevision === base.answerRevision + 1
                && accepted.versionRevision === base.versionRevision + 1
                && same(fixedFiles(accepted.files), fixedFiles(targetFiles))) {
              requireAnswerProfile(accepted, history.definition)
              adopt(current)
              continue
            }
          } catch (readError) { /* The original failure retains the draft. */ }
        }
        throw await handleFailure(error)
      }
    }
    // Avoid removing an edit made while cleanup was pending: removal completes
    // before a new local snapshot is scheduled by changed().
    if (!localDraft && !recoveryMismatch && !dirty()) {
      try { await draftStore.remove(recoveryKey()) } catch (error) { /* Server save remains valid. */ }
    }
    if (!snapshot && dirty()) return acknowledgedSave()
    announce(dirty() ? 'unsaved' : 'saved', dirty() ? 'Unsaved edits' : 'Saved')
    return clone(answer)
  }
  const flush = () => {
    clearTimers()
    if (config.preview) return enqueue(async () => ({ files: await durable(), locallyDurable: true }))
    if (terminal) return Promise.resolve(answer && clone(answer))
    return enqueue(() => acknowledgedSave())
  }
  const schedule = () => {
    if (disposed || terminal || conflict || config.preview) return
    timers.clearTimeout(idleTimer)
    idleTimer = timers.setTimeout(() => flush().catch(() => {
      if (!terminal && !conflict && !disposed) {
        retryTimer = timers.setTimeout(() => flush().catch(() => {}), 5000)
      }
    }), 1000)
    if (!maxTimer) maxTimer = timers.setTimeout(() => flush().catch(() => {}), 5000)
  }
  const changed = () => {
    if (disposed || terminal || conflict || submitting || initializing || historyBusy) return
    if (config.preview) { announce('preview', 'Disposable preview'); return }
    // Copy before awaiting IDB, so rapid edits cannot alter the persisted object.
    durable().catch(() => announce('local-unavailable', 'Local recovery is unavailable. Run requires an acknowledged server save.'))
    announce('unsaved', 'Unsaved edits')
    schedule()
  }
  const beforeRun = async () => {
    if (initializing || historyBusy) throw new Error('Wait for the coding project to finish loading or switching versions.')
    if (terminalUnsubmitted) throw new Error('The editor is an unsubmitted local copy. The accepted server project could not be safely displayed.')
    const runIdentity = identity()
    const runAnswer = answer && clone(answer)
    const runHistoryGeneration = historyGeneration
    if (terminal) return { files: clone(files()), locallyDurable: true, answer: runAnswer, ...runIdentity }
    if (config.preview) {
      try {
        const snapshot = await durable(clone(files()))
        announce('preview-saved', 'Practice saved locally')
        return { files: snapshot, locallyDurable: true, ...runIdentity }
      } catch (error) {
        announce('error', 'Local practice save failed. Code has not run.')
        throw error
      }
    }
    if (conflict || !initialized) throw new Error('Reload or finish initialization before running code.')
    const snapshot = clone(files())
    try {
      const committed = await durable(snapshot)
      return { files: committed, locallyDurable: true, answer: runAnswer, ...runIdentity }
    } catch (error) {
      clearTimers()
      const accepted = await enqueue(() => {
        // Local durability may fail after a history action selected another
        // version. Never write the old Run snapshot into that selected version.
        // Ordinary autosave revision advances on this same pin remain valid.
        if (historyGeneration !== runHistoryGeneration || answer?.versionId !== runAnswer?.versionId ||
            answer?.starterId !== runAnswer?.starterId || answer?.starterDigest !== runAnswer?.starterDigest ||
            !same(identity(), runIdentity)) throw new Error('The selected coding version changed before Run could be saved.')
        return acknowledgedSave(snapshot)
      })
      if (!accepted || !same(snapshot, fixedFiles(accepted.files))) {
        throw new Error('Code must be saved before Run because local recovery is unavailable.')
      }
      if (dirty()) schedule()
      return { files: clone(accepted.files), locallyDurable: false, answer: accepted, digest: accepted.digest, ...runIdentity }
    }
  }
  const operation = async (op, extras = {}) => {
    if (config.preview || terminal || conflict || initializing || historyBusy || submitting) throw new Error('History changes are unavailable here.')
    historyGeneration += 1
    historyBusy = true
    announce('history-busy', 'Saving and updating coding history…')
    try {
      await flush()
      return await enqueue(async () => {
      try {
        const body = { operation: op, versionId: answer.versionId,
          versionRevision: answer.versionRevision, answerRevision: answer.answerRevision, ...extras, ...identity() }
        const response = await request.post(config.saveUrl, body)
        const previousKey = recoveryKey()
        adopt(response, { replaceFiles: true })
        if (profile !== HTML_CSS_JS_PROFILE && previousKey !== recoveryKey()) await loadRecovery()
        return clone(answer)
      } catch (error) { throw await handleFailure(error) }
      })
    } finally {
      historyBusy = false
      announce(lastStatus, lastMessage)
    }
  }
  const recoverLocalDraft = async () => {
    if (!localDraft) throw new Error('No local draft is available.')
    if (!compatibleDraft(localDraft)) throw new Error('The recovery copy belongs to another coding runtime or version.')
    const recovered = clone(localDraft.files)
    const recoveredKey = recoveryKey()
    if (config.preview) {
      setFiles(recovered)
      localDraft = null
      announce('preview', 'Local practice draft restored. The assignment is unchanged.')
      return { files: recovered }
    }
    await operation('create_version', { name: 'Recovered local draft' })
    setFiles(recovered)
    changed()
    const result = await flush()
    if (!same(fixedFiles(result.files), fixedFiles(recovered))) throw new Error('The recovery copy has not been acknowledged.')
    localDraft = null
    try {
      await draftStore.remove(recoveredKey)
      await draftStore.remove(recoveredKey + ':current')
    } catch (error) { /* Recovery is acknowledged; retain leftover copies safely. */ }
    announce('saved', 'Recovered into a new saved version')
    return result
  }
  const inspectSource = async (sourceId, sourceKind = 'version') => {
    const response = await request.get(config.stateUrl, { params: { sourceId, sourceKind } })
    if (!response.data.success || !response.data.source) throw new Error('This history source could not be read.')
    return clone(response.data.source)
  }
  return {
    initialize, changed, flush, beforeRun, operation, recoverLocalDraft, inspectSource,
    isSettled: () => initialized && !initializing && !historyBusy && !dirty() && !pending && !conflict && !unsafeLocal,
    setSubmitting: value => {
      submitting = value
      if (value) announce('submitting', 'Saving code before submission…')
      else if (conflict) announce('conflict', 'Code changed in another tab. Reload to continue.')
      else if (terminal) announce(lastStatus, lastMessage)
      else announce(dirty() ? 'error' : 'saved', dirty() ? 'Code could not be saved. Retry before submitting.' : 'Saved')
    },
    dispose: () => { disposed = true; clearTimers() },
  }
}

export default createCodingPersistence
